import { useEffect, useState } from "react";
import {
  X,
  ShieldCheck,
  FileText,
  Loader2,
  Info,
  Save,
  Send,
} from "lucide-react";
import { ToastContainer } from "react-toastify";

const CreditBillModal = ({
  billOpen,
  closeBill,
  billRow,
  billingName,
  setBillingName,
  billingGst,
  setBillingGst,
  billRemarks,
  setBillRemarks,
  isOldBill,
  setIsOldBill,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  billType,
  setBillType,
  billAmount,
  setBillAmount,
  billLoading,
  billUrl,
  onFetchBill,
  onSaveInvoice,
  onSendToCustomer,
  setBillUrl,
}) => {
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (billOpen) {
      setShowPreview(false);
    }
  }, [billOpen]);

  useEffect(() => {
    if (billUrl) {
      setShowPreview(true);
    }
  }, [billUrl]);

  useEffect(() => {
    if (billOpen) {
      const today = new Date().toISOString().split("T")[0];
      setFromDate(today);
      setToDate(today);
      if (typeof setBillUrl === "function") {
        setBillUrl("");
      }
    }
  }, [billOpen]);

  if (!billOpen) return null;

  return (
    <>
    <ToastContainer/>

    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 montserrat-font-1"
      onClick={closeBill}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-[95%] max-w-3xl rounded-xl montserrat-font-1 shadow-lg flex flex-col max-h-[90vh]"
      >
        {/* HEADER */}
        <div className="flex justify-between items-center p-4 border-b">
          <h4 className="font-semibold text-lg">Generate Credit Bill</h4>
          <button onClick={closeBill}>
            <X size={18} />
          </button>
        </div>

        {/* BODY */}
        <div className="flex flex-col lg:flex-row gap-6 p-6 overflow-y-auto">
          {/* LEFT */}
          <div className="flex-1 space-y-6">
            {/* MOBILE + NAME */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Mobile
                </label>
                <div className="mt-1 border rounded-lg px-3 py-2 bg-gray-50 text-sm">
                  {billRow?.customer_mobile}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Billing Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={billingName}
                  onChange={(e) => setBillingName(e.target.value)}
                />
              </div>
            </div>

            {/* GST + REMARKS */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Billing GSTIN (optional)
                </label>
                <input
                  placeholder="22AAAAA0000A1Z5"
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={billingGst}
                  onChange={(e) => setBillingGst(e.target.value.toUpperCase())}
                />
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                  <Info size={12} />
                  Prefilled from customer, editable. Leave blank if not
                  applicable.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Remarks (optional)
                </label>
                <textarea
                  placeholder="text"
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={billRemarks}
                  onChange={(e) => setBillRemarks(e.target.value)}
                />
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                  <Info size={12} />
                  Prefilled from customer, editable. Leave blank if not
                  applicable.
                </p>
              </div>
            </div>

            {/* NEW / OLD BILL CARDS */}
            <div className="grid grid-cols-2 gap-4">
              {/* NEW BILL */}
              <div
                onClick={() => setIsOldBill("No")}
                className={`border rounded-xl p-4 cursor-pointer transition ${
                  isOldBill === "No"
                    ? "border-green-500 bg-green-50"
                    : "hover:border-gray-300"
                }`}
              >
                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={isOldBill === "No"} readOnly />
                    <span className="font-medium text-sm">New Bill</span>
                  </div>

                  <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Info size={12} />
                    Default
                  </span>
                </div>

                <p className="text-xs text-gray-500">
                  Generate a fresh credit bill. “Send to customer” stays
                  disabled.
                </p>
              </div>

              {/* OLD BILL */}
              <div
                onClick={() => setIsOldBill("Yes")}
                className={`border rounded-xl p-4 cursor-pointer transition ${
                  isOldBill === "Yes"
                    ? "border-green-500 bg-green-50"
                    : "hover:border-gray-300"
                }`}
              >
                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={isOldBill === "Yes"}
                      readOnly
                    />
                    <span className="font-medium text-sm">Old Bill</span>
                  </div>

                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <ShieldCheck size={12} />
                    Allowed to send
                  </span>
                </div>

                <p className="text-xs text-gray-500">
                  Enable this only if you’re sending a previously pending/old
                  bill.
                </p>
              </div>
            </div>

            {/* DATES */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  From Date
                </label>
                <input
                  type="date"
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={fromDate || ""}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  To Date
                </label>
                <input
                  type="date"
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={toDate || ""}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>

            {/* BILL TYPE */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Bill Type
                </label>

                <div className="flex gap-4 mt-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={billType === "auto"}
                      onChange={() => setBillType("auto")}
                    />
                    Auto Bill
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={billType === "custom"}
                      onChange={() => setBillType("custom")}
                    />
                    Custom Bill
                  </label>
                </div>

                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                  <Info size={12} />
                  Auto mode uses the customer's generated invoice amount. Choose
                  Custom to enter a different amount.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Bill Amount (₹)
                </label>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                  value={billAmount}
                  disabled={billType !== "custom"}
                  onChange={(e) => setBillAmount(e.target.value)}
                  placeholder={
                    billType === "custom"
                      ? "Enter amount (e.g. 100 or 1000)"
                      : "Amount will be fetched automatically"
                  }
                />
              </div>
            </div>

            {/* FETCH BUTTON */}
            {!billUrl && (
              <div className="flex justify-end gap-4">
                <button
                  onClick={closeBill}
                  className="bg-red-500 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                >
                  <X size={16} />
                  Close
                </button>

                <button
                  onClick={onFetchBill}
                  disabled={billLoading}
                  className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
                >
                  {billLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Fetching...
                    </>
                  ) : (
                    <>
                      <FileText size={16} />
                      Fetch Bill
                    </>
                  )}
                </button>
              </div>
            )}

            {/* RIGHT PREVIEW */}
            {billUrl && (
              <div className="flex-1 border rounded-xl p-4 bg-gray-50">
                <h4 className="text-sm font-semibold mb-3">Preview</h4>

                <div className="flex justify-center">
                  <img
                    src={`${billUrl}${billUrl.includes("?") ? "&" : "?"}t=${Date.now()}`}
                    alt="Invoice Preview"
                    className="max-h-[520px] object-contain rounded shadow"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div
          className={`p-4 border-t grid gap-3 ${
            billUrl ? "grid-cols-3" : "grid-cols-1"
          }`}
        >
          {billUrl && (
            <>
              <button
                onClick={closeBill}
                className="bg-red-500 text-white py-2 rounded-lg flex items-center justify-center gap-2"
              >
                <X size={16} />
                Close
              </button>

              <button
                onClick={onSaveInvoice}
                disabled={billLoading}
                className="bg-blue-500 text-white py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Save size={16} />
                Save
              </button>

              <button
                onClick={onSendToCustomer}
                disabled={billLoading}
                className="bg-green-500 text-white py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Send size={16} />
                Send
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default CreditBillModal;
